import type { ReactNode } from "react";
import { Text } from "@blycare/polar-ui";
import { LoadingSkeleton } from "../loading-skeleton";

type PageSkeleton = {
  loading: boolean;
  label: string;
  content: ReactNode;
};

type PageLayoutProps = {
  heading: string;
  actions?: ReactNode;
  inlineSize?: "small" | "base" | "large";
  skeleton?: PageSkeleton;
  children: ReactNode;
};

export function PageLayout({ heading, actions, inlineSize, skeleton, children }: PageLayoutProps) {
  return (
    <div className="mx-auto max-w-249.5 px-2">
      <s-page {...(inlineSize ? { inlineSize } : {})}>
        <div className="py-2">
          <div className="mb-6">
            <s-stack
              direction="inline"
              justifyContent="space-between"
              gap="small"
              alignItems="center"
            >
              <Text as="h1" variant="headingLg">
                {heading}
              </Text>
              {actions}
            </s-stack>
          </div>
          {skeleton ? (
            <PageSkeletonContent {...skeleton}>{children}</PageSkeletonContent>
          ) : (
            children
          )}
        </div>
      </s-page>
    </div>
  );
}

function PageSkeletonContent({
  loading,
  label,
  content,
  children,
}: PageSkeleton & { children: ReactNode }) {
  return (
    <div
      {...(loading
        ? {
            role: "status" as const,
            "aria-busy": true,
            "aria-live": "polite" as const,
          }
        : {})}
    >
      {loading ? <span className="sr-only">{label}</span> : null}
      <LoadingSkeleton loading={loading} skeleton={content}>
        {children}
      </LoadingSkeleton>
    </div>
  );
}
